/**
 * Minimal glass navbar — top of every login-app route.
 *
 * Pattern matches the `galaxy-interactive-hero-section` template
 * Karim approved, stripped to the bits that make sense for an
 * auth-gate page:
 *   - DYMAXION logo mark (links to public site)
 *   - no marketing nav (Features/Pricing/etc — users aren't signed in)
 *   - "Back to dymaxion.com.au" escape hatch
 *
 * Glass surface: rgba(13, 13, 24, 0.3) + backdrop-blur(8px) + rounded
 * bottom corners.
 */

export interface NavBarProps {
  publicSiteUrl: string;
  contactUrl: string;
}

export function NavBar({ publicSiteUrl, contactUrl }: NavBarProps) {
  return (
    <nav
      className="fixed top-0 left-0 right-0 z-20 border-b border-white/5"
      style={{
        backgroundColor: 'rgba(13, 13, 24, 0.3)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderRadius: '0 0 15px 15px',
      }}
    >
      <div className="container mx-auto flex items-center justify-between px-4 py-4 md:px-6 lg:px-8">
        <a
          href={publicSiteUrl}
          className="flex items-center gap-3 text-white transition-opacity hover:opacity-80"
          aria-label="DYMAXION home"
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32ZM12.4306 9.70695C12.742 9.33317 13.2633 9.30058 13.6052 9.62118L19.1798 14.8165C19.4894 15.1054 19.4894 15.5841 19.1798 15.873L13.6052 21.0683C13.2633 21.3889 12.742 21.3563 12.4306 19.9991V9.70695Z"
              fill="currentColor"
            />
          </svg>
          <span className="hidden text-sm font-semibold tracking-wider text-white sm:inline">
            DYMAXION
          </span>
        </a>

        <div className="flex items-center gap-4 md:gap-6">
          <a
            href={publicSiteUrl}
            className="hidden text-sm text-gray-300 transition-colors hover:text-white sm:block"
          >
            {hostnameOf(publicSiteUrl)}
          </a>
          <a
            href={contactUrl}
            className="hidden text-sm text-gray-300 transition-colors hover:text-white md:block"
          >
            Contact
          </a>
        </div>
      </div>
    </nav>
  );
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
